/**
 * Drawing helpers shared by every render module: cached fonts, cached glow
 * sprites (radial gradients rendered once to offscreen canvases and drawn
 * with `drawImage` under 'lighter', never `shadowBlur`), drop shadows,
 * rounded rects, the shield hex pattern, easing and a cheap 1D noise.
 *
 * Everything here is allocation-free after warm-up so the hot layers can
 * call it per entity per frame.
 */

import { UNIT_PX } from '@shared/index.js';
import { hexToRgb } from '../palette.js';

export type Ctx = CanvasRenderingContext2D;

export const TAU = Math.PI * 2;
export const UNIT = UNIT_PX;

/** Height (units) above the feet where beams and sprays leave the staff tip. */
export const STAFF_TIP_H = 0.6;
/** Height (units) of a wizard body from feet to hat tip, for upright drawing and height sorting. */
export const WIZARD_H = 1.35;
/** Height of a wall / barrier front face in units. */
export const WALL_H = 0.7;
/** Pillar height in units. */
export const PILLAR_H = 1.6;

// ------------------------------------------------------------------ fonts

const FONT_FAMILY = "system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const fontCache = new Map<number, string>();

/** `weight` 400|600|700|800; `size` in px. Cached. */
export function font(size: number, weight = 600): string {
  const key = weight * 1000 + Math.round(size);
  let f = fontCache.get(key);
  if (!f) {
    f = `${weight} ${Math.round(size)}px ${FONT_FAMILY}`;
    fontCache.set(key, f);
  }
  return f;
}

// ------------------------------------------------------------------ glow sprites

const GLOW_SIZES = [16, 32, 64, 128, 256] as const;
const glowCache = new Map<string, HTMLCanvasElement[]>();

function makeGlow(hex: string, size: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const g = c.getContext('2d');
  if (g) {
    const [r, gg, b] = hexToRgb(hex);
    const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grad.addColorStop(0, `rgba(${r},${gg},${b},0.9)`);
    grad.addColorStop(0.35, `rgba(${r},${gg},${b},0.45)`);
    grad.addColorStop(1, `rgba(${r},${gg},${b},0)`);
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
  }
  return c;
}

/**
 * Draw a radial glow of `diameter` px centred on (x, y). Caller sets
 * globalCompositeOperation ('lighter' for magic) and globalAlpha.
 */
export function drawGlow(ctx: Ctx, hex: string, x: number, y: number, diameter: number): void {
  let sprites = glowCache.get(hex);
  if (!sprites) {
    sprites = GLOW_SIZES.map((s) => makeGlow(hex, s));
    glowCache.set(hex, sprites);
  }
  // Pick the smallest sprite at least as big as requested so scaling only shrinks.
  let i = 0;
  while (i < GLOW_SIZES.length - 1 && (GLOW_SIZES[i] as number) < diameter) i++;
  const sprite = sprites[i] as HTMLCanvasElement;
  const half = diameter / 2;
  ctx.drawImage(sprite, x - half, y - half, diameter, diameter);
}

// ------------------------------------------------------------------ primitives

export function drawShadow(ctx: Ctx, x: number, y: number, rx: number, ry: number, alpha = 0.45): void {
  ctx.globalAlpha = alpha;
  ctx.fillStyle = '#000000';
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, TAU);
  ctx.fill();
  ctx.globalAlpha = 1;
}

export function roundRectPath(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.arcTo(x + w, y, x + w, y + rr, rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
  ctx.lineTo(x + rr, y + h);
  ctx.arcTo(x, y + h, x, y + h - rr, rr);
  ctx.lineTo(x, y + rr);
  ctx.arcTo(x, y, x + rr, y, rr);
  ctx.closePath();
}

export function polygonPath(ctx: Ctx, x: number, y: number, r: number, n: number, rot = 0): void {
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const a = rot + (TAU * i) / n;
    const px = x + Math.cos(a) * r;
    const py = y + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

export function circlePath(ctx: Ctx, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
}

/** Text with a dark outline so it reads on any floor. */
export function outlinedText(ctx: Ctx, text: string, x: number, y: number, fill: string, outline = 'rgba(0,0,0,0.85)', width = 3): void {
  ctx.lineJoin = 'round';
  ctx.lineWidth = width;
  ctx.strokeStyle = outline;
  ctx.strokeText(text, x, y);
  ctx.fillStyle = fill;
  ctx.fillText(text, x, y);
}

// ------------------------------------------------------------------ hex pattern (shield surfaces)

let hexPatternCanvas: HTMLCanvasElement | null = null;
const hexPatternCache = new WeakMap<Ctx, CanvasPattern>();

function makeHexTile(): HTMLCanvasElement {
  const size = 24;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const g = c.getContext('2d');
  if (g) {
    g.strokeStyle = 'rgba(255,255,255,0.35)';
    g.lineWidth = 1;
    const r = 6;
    const draw = (cx: number, cy: number): void => {
      g.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i + Math.PI / 6;
        const px = cx + Math.cos(a) * r;
        const py = cy + Math.sin(a) * r;
        if (i === 0) g.moveTo(px, py);
        else g.lineTo(px, py);
      }
      g.closePath();
      g.stroke();
    };
    draw(6, 6);
    draw(18, 6);
    draw(12, 16);
    draw(0, 16);
    draw(24, 16);
  }
  return c;
}

/** Translucent hex-tile pattern for barriers and bubbles (DESIGN 6.3 "hex ring"). */
export function hexPattern(ctx: Ctx): CanvasPattern | null {
  const hit = hexPatternCache.get(ctx);
  if (hit) return hit;
  if (!hexPatternCanvas) hexPatternCanvas = makeHexTile();
  const p = ctx.createPattern(hexPatternCanvas, 'repeat');
  if (p) hexPatternCache.set(ctx, p);
  return p;
}

// ------------------------------------------------------------------ easing and noise (DOM-free, see mathfx.ts)

export { easeOutCubic, easeOutBack, easeInQuad, clamp01, noise1D, hashRand } from './mathfx.js';
